export const introductionToPsychologyCourse = {
  id: "introduction-to-psychology-2024",
  title: "Introduction to Psychology: Mind, Behavior & Mental Processes",
  shortDescription: "Explore the scientific study of human behavior, mental processes, and the biological and environmental factors that influence them.",
  description: "Comprehensive introduction to psychological science covering major areas including biological psychology, cognition, learning, memory, personality, abnormal psychology, and social psychology. Students will learn about research methods, theoretical perspectives, and practical applications of psychological principles in everyday life.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from psychology professors and mental health professionals",
  thumbnailUrl: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand major psychological theories and research methods",
    "Analyze biological bases of behavior and mental processes",
    "Examine cognitive processes including memory, thinking, and perception",
    "Evaluate learning theories and behavior modification principles",
    "Understand personality development and individual differences",
    "Analyze abnormal behavior and mental health disorders",
    "Examine social influences on behavior and attitudes",
    "Apply psychological principles to real-world situations"
  ],

  prerequisites: [
    "High school education",
    "Basic understanding of scientific method",
    "Interest in human behavior and mental processes"
  ],

  skills: [
    "Research Methods",
    "Statistical Analysis",
    "Critical Thinking",
    "Behavioral Observation",
    "Data Interpretation",
    "Psychological Assessment",
    "Communication Skills",
    "Ethical Reasoning"
  ],

  marketData: {
    averageSalary: 58000,
    salaryRange: {
      min: 40000,
      max: 85000
    },
    jobDemand: 4.2,
    marketTrend: "growing",
    careerPaths: [
      "Clinical Psychologist",
      "Counselor",
      "Research Assistant",
      "Human Resources Specialist",
      "Social Worker",
      "Mental Health Technician"
    ],
    relatedSkills: [
      "Research Methods",
      "Statistics",
      "Counseling",
      "Assessment",
      "Data Analysis",
      "Communication"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "psychology-foundations",
      title: "Foundations of Psychology",
      description: "Introduction to psychology as a science, major perspectives, and research methods",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "psychology-intro",
          title: "What is Psychology?",
          description: "Overview of psychology as the scientific study of behavior and mental processes",
          type: "video",
          duration: 30
        },
        {
          id: "research-methods",
          title: "Research Methods in Psychology",
          description: "Scientific approaches to studying behavior and mental processes",
          type: "text",
          duration: 45,
          textContent: `# Research Methods in Psychology

## The Scientific Study of Mind and Behavior

### Defining Psychology

Psychology is the scientific study of behavior and mental processes. It encompasses both observable actions and internal experiences such as thoughts, emotions, and perceptions.

**Key Areas of Study:**
- **Behavior**: Observable actions and responses
- **Mental Processes**: Thoughts, emotions, memories, perceptions
- **Biological Bases**: Brain, nervous system, genetics
- **Social Influences**: Cultural and environmental factors

### Historical Perspectives

**Major Schools of Thought**

**Structuralism (Wilhelm Wundt)**
- Focus on breaking down mental processes into basic elements
- Method: Introspection
- Goal: Understand the structure of consciousness

**Functionalism (William James)**
- Focus on the purpose and adaptation of mental processes
- Influenced by Darwin's evolution theory
- Goal: Understand how mental processes help survival

**Behaviorism (John Watson, B.F. Skinner)**
- Focus on observable behavior only
- Method: Controlled experiments
- Goal: Predict and control behavior

**Humanistic Psychology (Carl Rogers, Abraham Maslow)**
- Focus on human potential and self-actualization
- Emphasis on conscious experience and free will
- Goal: Promote human flourishing

**Cognitive Psychology**
- Focus on mental processes like thinking and memory
- Method: Information processing models
- Goal: Understand how people process information

### Contemporary Perspectives

**Biological Perspective**
- Emphasizes physical bases of behavior
- Studies brain, nervous system, hormones, genetics
- Methods: Brain imaging, genetic analysis

**Cognitive Perspective**
- Focuses on thinking, memory, perception, problem-solving
- Studies information processing
- Methods: Experimental tasks, computer models

**Behavioral Perspective**
- Emphasizes learning and environmental influences
- Studies conditioning and reinforcement
- Methods: Controlled experiments

**Psychodynamic Perspective**
- Emphasizes unconscious processes and early experience
- Based on Freudian theory
- Methods: Case studies, psychoanalysis

**Humanistic Perspective**
- Emphasizes human potential and personal growth
- Focus on conscious experience
- Methods: Phenomenological approaches

**Sociocultural Perspective**
- Emphasizes social and cultural influences
- Studies how context shapes behavior
- Methods: Cross-cultural research

### Research Methods

**Descriptive Methods**

**Naturalistic Observation**
- Observing behavior in natural settings
- **Advantages**: High ecological validity
- **Disadvantages**: No control over variables
- **Example**: Studying playground aggression

**Case Studies**
- In-depth study of individual cases
- **Advantages**: Rich, detailed information
- **Disadvantages**: Limited generalizability
- **Example**: Phineas Gage brain injury case

**Surveys**
- Collecting data through questionnaires or interviews
- **Advantages**: Large sample sizes possible
- **Disadvantages**: Self-report bias
- **Example**: Studying attitudes toward mental health

**Correlational Research**
- Examining relationships between variables
- **Advantages**: Can study variables that can't be manipulated
- **Disadvantages**: Cannot establish causation
- **Example**: Relationship between sleep and academic performance

**Experimental Methods**

**Controlled Experiments**
- Manipulating independent variables to observe effects
- **Advantages**: Can establish cause-and-effect relationships
- **Disadvantages**: May lack ecological validity
- **Components**:
  - **Independent Variable**: What researcher manipulates
  - **Dependent Variable**: What researcher measures
  - **Control Group**: Comparison group
  - **Experimental Group**: Receives treatment

**Quasi-Experimental Designs**
- Similar to experiments but lack random assignment
- Used when true experiments aren't possible
- **Example**: Studying effects of a new teaching method

### Ethical Considerations

**Key Principles**

**Informed Consent**
- Participants must understand study procedures
- Must be able to withdraw at any time
- Special considerations for vulnerable populations

**Confidentiality**
- Protecting participant privacy
- Anonymous vs. confidential data
- Secure data storage and sharing

**Risk-Benefit Analysis**
- Minimizing potential harm
- Ensuring benefits outweigh risks
- Physical and psychological harm considerations

**Deception and Debriefing**
- When deception is necessary and justified
- Full explanation after participation
- Ensuring no lasting negative effects

**Special Populations**
- Additional protections for children
- Considerations for mentally ill participants
- Institutional Review Board oversight

### Statistical Concepts

**Descriptive Statistics**
- **Measures of Central Tendency**: Mean, median, mode
- **Measures of Variability**: Range, standard deviation
- **Purpose**: Summarize and describe data

**Inferential Statistics**
- **Hypothesis Testing**: Testing predictions
- **Significance Levels**: Probability of chance results
- **Effect Size**: Practical importance of findings

**Correlation Coefficients**
- Measure strength and direction of relationships
- Range from -1.00 to +1.00
- Correlation does not equal causation

### Critical Thinking in Psychology

**Evaluating Research**
- **Sample Size**: Larger samples more reliable
- **Replication**: Can findings be repeated?
- **Peer Review**: Has study been evaluated by experts?
- **Bias**: Are there conflicts of interest?

**Common Pitfalls**
- **Confirmation Bias**: Seeking confirming evidence
- **Correlation-Causation Error**: Assuming correlation implies causation
- **Overgeneralization**: Applying findings too broadly
- **Anecdotal Evidence**: Relying on personal stories

Understanding research methods is crucial for evaluating psychological claims and becoming an informed consumer of psychological research.`
        },
        {
          id: "research-design",
          title: "Design Your Own Study",
          description: "Interactive exercise in designing psychological research studies",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "research-design",
            instructions: "Design a psychological research study by choosing appropriate methods, variables, and ethical considerations.",
            scenarios: [
              {
                id: "memory-study",
                question: "How does sleep deprivation affect memory performance?",
                methodOptions: ["Experiment", "Correlation", "Case study", "Survey"],
                variables: {
                  independent: "Hours of sleep",
                  dependent: "Memory test scores",
                  controls: ["Age", "Education level", "Time of day"]
                },
                ethicalConsiderations: ["Informed consent", "Potential fatigue harm", "Debriefing"]
              },
              {
                id: "social-media-study",
                question: "What is the relationship between social media use and depression?",
                methodOptions: ["Experiment", "Correlation", "Longitudinal", "Cross-sectional"],
                variables: {
                  independent: "Social media usage time",
                  dependent: "Depression scores",
                  controls: ["Age", "Social support", "Personality"]
                },
                ethicalConsiderations: ["Privacy", "Mental health screening", "Referral resources"]
              }
            ],
            hints: [
              "Consider whether you can manipulate variables ethically",
              "Think about what confounding variables need to be controlled",
              "Consider the ethical implications of your research design"
            ]
          }
        }
      ],
      quiz: {
        id: "psychology-foundations-quiz",
        title: "Psychology Foundations Quiz",
        description: "Test your understanding of psychology's history, perspectives, and research methods",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which research method can establish cause-and-effect relationships?",
            options: [
              "Correlational studies",
              "Naturalistic observation",
              "Controlled experiments",
              "Case studies"
            ],
            correctAnswer: 2,
            explanation: "Controlled experiments are the only research method that can establish cause-and-effect relationships because they involve manipulation of variables and control of confounding factors."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The behavioral perspective in psychology emphasizes:",
            options: [
              "Unconscious motivations",
              "Observable behavior and learning",
              "Biological factors",
              "Cognitive processes"
            ],
            correctAnswer: 1,
            explanation: "The behavioral perspective focuses on observable behavior and how it is learned through conditioning and environmental influences."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What does it mean that correlation does not equal causation?",
            options: [
              "Correlations are always negative",
              "A relationship between variables doesn't prove one causes the other",
              "Correlations are unreliable",
              "Only experiments show relationships"
            ],
            correctAnswer: 1,
            explanation: "Correlation shows a relationship between variables, but this doesn't prove that one variable causes changes in the other. There could be third variables or the causation could go in the opposite direction."
          }
        ]
      }
    },

    {
      id: "biological-psychology",
      title: "Biological Bases of Behavior",
      description: "Exploring the relationship between brain, nervous system, and behavior",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "nervous-system",
          title: "The Nervous System",
          description: "Structure and function of the central and peripheral nervous systems",
          type: "video",
          duration: 35
        },
        {
          id: "brain-behavior",
          title: "Brain Structure and Function",
          description: "How different brain regions control behavior and mental processes",
          type: "text",
          duration: 50,
          textContent: `# Brain Structure and Function

## The Biological Foundation of Psychology

### The Nervous System Overview

The nervous system is the body's communication network, processing information and coordinating responses to internal and external stimuli.

**Major Divisions:**
- **Central Nervous System (CNS)**: Brain and spinal cord
- **Peripheral Nervous System (PNS)**: All nerves outside the CNS

### Neurons: The Basic Units

**Neuron Structure**
- **Cell Body (Soma)**: Contains nucleus and most organelles
- **Dendrites**: Receive signals from other neurons
- **Axon**: Transmits signals to other neurons
- **Myelin Sheath**: Insulates axon, speeds transmission
- **Synapses**: Gaps between neurons where communication occurs

**Types of Neurons**
- **Sensory Neurons**: Carry information to CNS
- **Motor Neurons**: Carry commands from CNS to muscles
- **Interneurons**: Connect neurons within CNS

**Neural Communication**
1. **Resting Potential**: Neuron at rest (-70mV)
2. **Action Potential**: Electrical signal travels down axon
3. **Synaptic Transmission**: Chemical communication between neurons
4. **Neurotransmitters**: Chemical messengers

### Major Neurotransmitters

**Acetylcholine (ACh)**
- **Function**: Muscle movement, memory, attention
- **Disorders**: Alzheimer's disease (decreased ACh)

**Dopamine**
- **Function**: Reward, motivation, motor control
- **Disorders**: Parkinson's (low), Schizophrenia (high)

**Serotonin**
- **Function**: Mood, sleep, appetite
- **Disorders**: Depression (low serotonin)

**GABA**
- **Function**: Inhibitory neurotransmitter, anxiety control
- **Disorders**: Anxiety disorders (low GABA)

**Norepinephrine**
- **Function**: Alertness, arousal, fight-or-flight
- **Disorders**: Depression (low levels)

### Brain Structure and Function

**Hindbrain (Brainstem)**
- **Medulla**: Controls vital functions (breathing, heart rate)
- **Pons**: Sleep, arousal, facial movement
- **Cerebellum**: Balance, coordination, motor learning

**Midbrain**
- **Reticular Formation**: Consciousness, arousal
- **Substantia Nigra**: Dopamine production

**Forebrain**

**Thalamus**
- Relay station for sensory information
- Gateway to cerebral cortex

**Hypothalamus**
- Controls hormones, body temperature, hunger, thirst
- Connects nervous and endocrine systems

**Limbic System**
- **Hippocampus**: Memory formation
- **Amygdala**: Emotion, fear processing
- **Limbic System**: Emotion and motivation

### Cerebral Cortex

**Lobes of the Cortex**

**Frontal Lobe**
- **Primary Motor Cortex**: Voluntary movement
- **Prefrontal Cortex**: Executive functions, planning, personality
- **Broca's Area**: Speech production

**Parietal Lobe**
- **Primary Somatosensory Cortex**: Touch, temperature, pain
- **Spatial processing and attention

**Temporal Lobe**
- **Primary Auditory Cortex**: Hearing
- **Wernicke's Area**: Language comprehension
- **Hippocampus**: Memory

**Occipital Lobe**
- **Primary Visual Cortex**: Vision
- **Visual processing areas

### Hemispheric Specialization

**Left Hemisphere**
- Language processing (most people)
- Logical, analytical thinking
- Sequential processing

**Right Hemisphere**
- Spatial processing
- Artistic, creative abilities
- Holistic processing

**Corpus Callosum**
- Connects left and right hemispheres
- Allows communication between sides

### Plasticity and Neurogenesis

**Brain Plasticity**
- Brain's ability to change and adapt
- Critical periods for development
- Recovery from brain injury

**Neurogenesis**
- Formation of new neurons
- Occurs throughout life in hippocampus
- Enhanced by exercise, learning, environmental enrichment

### Methods of Studying the Brain

**Lesion Studies**
- Study patients with brain damage
- Historical cases like Phineas Gage

**Brain Imaging Techniques**
- **CT Scan**: X-ray images of brain structure
- **MRI**: Detailed structural images
- **fMRI**: Shows brain activity in real-time
- **PET Scan**: Shows metabolic activity
- **EEG**: Measures electrical activity

### Genetics and Behavior

**Nature vs. Nurture**
- Genetic influences on behavior
- Environmental factors
- Gene-environment interactions

**Behavioral Genetics Methods**
- **Twin Studies**: Compare identical and fraternal twins
- **Adoption Studies**: Separate genetic and environmental influences
- **Heritability**: Proportion of trait variation due to genetics

### Endocrine System

**Hormone Functions**
- Chemical messengers in bloodstream
- Slower but longer-lasting than neurotransmitters
- Regulate metabolism, growth, reproduction, stress response

**Major Glands**
- **Pituitary**: "Master gland" controls other glands
- **Thyroid**: Metabolism regulation
- **Adrenals**: Stress hormones (cortisol, adrenaline)
- **Gonads**: Sex hormones (testosterone, estrogen)

Understanding the biological bases of behavior helps explain how physical processes in the brain and body influence our thoughts, emotions, and actions.`
        },
        {
          id: "brain-mapping",
          title: "Virtual Brain Exploration",
          description: "Interactive exploration of brain regions and their functions",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "brain-mapping",
            instructions: "Explore different brain regions and match them with their primary functions and associated disorders.",
            brainRegions: [
              {
                id: "hippocampus",
                name: "Hippocampus",
                functions: ["Memory formation", "Spatial navigation", "Learning"],
                disorders: ["Alzheimer's disease", "Amnesia", "PTSD"]
              },
              {
                id: "amygdala",
                name: "Amygdala",
                functions: ["Fear processing", "Emotional memory", "Fight-or-flight response"],
                disorders: ["Anxiety disorders", "PTSD", "Phobias"]
              },
              {
                id: "prefrontal-cortex",
                name: "Prefrontal Cortex",
                functions: ["Executive functions", "Decision making", "Personality"],
                disorders: ["ADHD", "Depression", "Schizophrenia"]
              }
            ],
            hints: [
              "Consider the location of each brain region",
              "Think about what happens when these areas are damaged",
              "Connect structure to function"
            ]
          }
        }
      ],
      quiz: {
        id: "biological-psychology-quiz",
        title: "Biological Psychology Quiz",
        description: "Test your knowledge of brain structure, function, and neural processes",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which brain structure is primarily responsible for memory formation?",
            options: [
              "Amygdala",
              "Hippocampus",
              "Thalamus",
              "Cerebellum"
            ],
            correctAnswer: 1,
            explanation: "The hippocampus is crucial for forming new memories and is one of the first brain regions affected in Alzheimer's disease."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the function of neurotransmitters?",
            options: [
              "Generate electrical signals",
              "Provide energy to neurons",
              "Carry chemical messages between neurons",
              "Protect neurons from damage"
            ],
            correctAnswer: 2,
            explanation: "Neurotransmitters are chemical messengers that carry signals across synapses from one neuron to another."
          }
        ]
      }
    },

    {
      id: "learning-and-memory",
      title: "Learning and Memory",
      description: "How we acquire new behaviors and retain information",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "learning-theories",
          title: "Theories of Learning",
          description: "Classical conditioning, operant conditioning, and observational learning",
          type: "video",
          duration: 35
        },
        {
          id: "memory-processes",
          title: "Memory Systems and Processes",
          description: "Encoding, storage, and retrieval of information",
          type: "text",
          duration: 50,
          textContent: `# Memory Systems and Processes\n\n## Types of Memory\n\n### Sensory Memory\n- Ultra-brief storage of sensory information\n- Iconic memory (visual) and echoic memory (auditory)\n- Duration: Less than 1 second\n\n### Short-Term Memory (STM)\n- Limited capacity storage system\n- Miller's Magic Number: 7±2 items\n- Duration: 15-30 seconds without rehearsal\n\n### Long-Term Memory (LTM)\n- Unlimited capacity storage system\n- Permanent storage of information\n- Types: Explicit (declarative) and implicit (procedural)\n\n## Memory Processes\n\n### Encoding\n- **Automatic Processing**: Effortless encoding of space, time, frequency\n- **Effortful Processing**: Requires attention and effort\n- **Levels of Processing**: Shallow vs. deep processing\n\n### Storage\n- **Consolidation**: Strengthening neural connections\n- **Organization**: Hierarchical and semantic networks\n- **Schemas**: Mental frameworks for organizing information\n\n### Retrieval\n- **Recall**: Retrieving information without cues\n- **Recognition**: Identifying previously learned information\n- **Cues**: Environmental and internal triggers\n\n## Forgetting and Memory Errors\n\n### Forgetting Theories\n- **Decay Theory**: Memory traces fade over time\n- **Interference Theory**: Other memories interfere\n- **Retrieval Failure**: Information available but not accessible\n\n### Memory Distortions\n- **False Memories**: Remembering events that didn't occur\n- **Source Amnesia**: Forgetting the source of information\n- **Suggestibility**: Memory influenced by leading questions\n\nUnderstanding memory helps explain learning, decision-making, and cognitive functioning.`
        }
      ],
      quiz: {
        id: "learning-memory-quiz",
        title: "Learning and Memory Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the typical capacity limit of short-term memory?",
            options: ["5±2 items", "7±2 items", "9±2 items", "11±2 items"],
            correctAnswer: 1,
            explanation: "According to Miller's research, short-term memory can typically hold 7±2 items (between 5-9 items)."
          }
        ]
      }
    },

    {
      id: "cognition-and-intelligence",
      title: "Cognition and Intelligence",
      description: "Thinking, problem-solving, and individual differences in cognitive ability",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "thinking-processes",
          title: "Thinking and Problem Solving",
          description: "Cognitive processes involved in reasoning and decision-making",
          type: "video",
          duration: 30
        },
        {
          id: "intelligence-theories",
          title: "Theories of Intelligence",
          description: "Different approaches to understanding cognitive ability",
          type: "text",
          duration: 45,
          textContent: `# Theories of Intelligence\n\n## Traditional Approaches\n\n### Spearman's g Factor\n- General intelligence underlying all cognitive abilities\n- Factor analysis reveals common factor across tests\n- Correlation between different cognitive tasks\n\n### Thurstone's Primary Mental Abilities\n- Seven distinct mental abilities\n- Word fluency, verbal comprehension, spatial visualization\n- Number facility, inductive reasoning, memory, perceptual speed\n\n## Contemporary Theories\n\n### Gardner's Multiple Intelligences\n- **Linguistic**: Language and verbal skills\n- **Logical-Mathematical**: Logical reasoning and math\n- **Spatial**: Visual-spatial processing\n- **Musical**: Musical ability and rhythm\n- **Bodily-Kinesthetic**: Physical coordination and movement\n- **Interpersonal**: Understanding others\n- **Intrapersonal**: Self-awareness and understanding\n- **Naturalistic**: Understanding nature and environment\n\n### Sternberg's Triarchic Theory\n- **Analytical Intelligence**: Academic problem-solving\n- **Creative Intelligence**: Novel problem-solving\n- **Practical Intelligence**: Real-world problem-solving\n\n## Intelligence Testing\n\n### Historical Development\n- **Binet-Simon Scale**: First intelligence test\n- **Stanford-Binet**: Adaptation for American children\n- **Wechsler Scales**: Separate verbal and performance IQ\n\n### IQ Scores\n- **Deviation IQ**: Standard scores with mean 100, SD 15\n- **Normal Distribution**: Bell curve of intelligence scores\n- **Percentile Ranks**: Position relative to norm group\n\n## Factors Affecting Intelligence\n\n### Genetic Influences\n- Twin studies show heritability estimates\n- Adoption studies separate genes from environment\n- Molecular genetics identifying specific genes\n\n### Environmental Factors\n- **Nutrition**: Malnutrition affects cognitive development\n- **Education**: Schooling improves cognitive abilities\n- **Socioeconomic Status**: Resources and opportunities\n- **Cultural Factors**: Values and practices affecting cognition\n\n### Flynn Effect\n- IQ scores increasing over time\n- Improved nutrition, education, technology\n- Cultural changes in abstract thinking\n\nIntelligence is multifaceted and influenced by both genetic and environmental factors.`
        }
      ],
      quiz: {
        id: "cognition-intelligence-quiz",
        title: "Cognition and Intelligence Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Gardner's theory, how many types of intelligence are there?",
            options: ["5", "7", "8", "10"],
            correctAnswer: 2,
            explanation: "Gardner originally proposed 8 types of intelligence in his multiple intelligences theory."
          }
        ]
      }
    },

    {
      id: "personality",
      title: "Personality Psychology",
      description: "Individual differences in patterns of thinking, feeling, and behaving",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "personality-theories",
          title: "Major Personality Theories",
          description: "Psychoanalytic, humanistic, and trait approaches to personality",
          type: "video",
          duration: 35
        },
        {
          id: "personality-assessment",
          title: "Personality Assessment",
          description: "Methods for measuring and evaluating personality",
          type: "text",
          duration: 45,
          textContent: `# Personality Assessment\n\n## Theoretical Approaches\n\n### Psychoanalytic Theory (Freud)\n- **Structure**: Id, ego, superego\n- **Psychosexual Stages**: Oral, anal, phallic, latency, genital\n- **Defense Mechanisms**: Repression, projection, sublimation\n- **Unconscious**: Hidden motivations and conflicts\n\n### Humanistic Theories\n- **Carl Rogers**: Self-concept and unconditional positive regard\n- **Abraham Maslow**: Hierarchy of needs and self-actualization\n- **Emphasis**: Human potential and personal growth\n\n### Trait Theories\n- **Allport**: Cardinal, central, and secondary traits\n- **Cattell**: 16 personality factors\n- **Big Five Model**: Openness, conscientiousness, extraversion, agreeableness, neuroticism\n\n## Personality Assessment Methods\n\n### Self-Report Inventories\n- **MMPI**: Minnesota Multiphasic Personality Inventory\n- **NEO-PI-R**: Measures Big Five traits\n- **16PF**: Cattell's 16 personality factors\n\n**Advantages:**\n- Standardized and reliable\n- Large norm groups for comparison\n- Objective scoring\n\n**Limitations:**\n- Social desirability bias\n- Self-awareness limitations\n- Cultural considerations\n\n### Projective Tests\n- **Rorschach Inkblot Test**: Interpret ambiguous stimuli\n- **TAT**: Thematic Apperception Test with pictures\n- **Draw-a-Person Test**: Projective drawing assessment\n\n**Advantages:**\n- Less subject to faking\n- Reveals unconscious processes\n- Rich, qualitative information\n\n**Limitations:**\n- Lower reliability and validity\n- Subjective interpretation\n- Time-consuming administration\n\n### Behavioral Observations\n- **Structured Situations**: Controlled behavioral samples\n- **Naturalistic Observation**: Real-world behavior\n- **Peer Ratings**: Others' perceptions of personality\n\n## Big Five Model\n\n### Openness to Experience\n- Creativity, curiosity, aesthetic appreciation\n- Intellectual engagement and novelty seeking\n\n### Conscientiousness\n- Organization, responsibility, self-discipline\n- Achievement orientation and dependability\n\n### Extraversion\n- Sociability, assertiveness, positive emotions\n- Energy level and social engagement\n\n### Agreeableness\n- Trust, cooperation, empathy\n- Concern for others and harmony\n\n### Neuroticism\n- Emotional instability, anxiety, vulnerability\n- Stress reactivity and negative emotions\n\n## Personality Development\n\n### Stability vs. Change\n- **Rank-order Stability**: Relative position remains consistent\n- **Mean-level Changes**: Average levels change with age\n- **Individual Differences**: Some people change more than others\n\n### Factors Influencing Development\n- **Genetics**: Heritability estimates around 40-60%\n- **Environment**: Family, peers, culture, life experiences\n- **Gene-Environment Interactions**: How genes respond to environment\n\nPersonality psychology helps understand individual differences and predict behavior across situations.`
        }
      ],
      quiz: {
        id: "personality-quiz",
        title: "Personality Psychology Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which of the Big Five personality traits involves emotional stability?",
            options: ["Openness", "Conscientiousness", "Extraversion", "Neuroticism"],
            correctAnswer: 3,
            explanation: "Neuroticism measures emotional instability, with low neuroticism indicating emotional stability and high neuroticism indicating emotional volatility."
          }
        ]
      }
    },

    {
      id: "abnormal-psychology",
      title: "Abnormal Psychology",
      description: "Understanding psychological disorders and mental health",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "defining-abnormality",
          title: "Defining Abnormal Behavior",
          description: "Criteria for distinguishing normal from abnormal behavior",
          type: "video",
          duration: 30
        },
        {
          id: "psychological-disorders",
          title: "Major Psychological Disorders",
          description: "Classification and symptoms of common mental health conditions",
          type: "text",
          duration: 50,
          textContent: `# Major Psychological Disorders\n\n## Defining Abnormality\n\n### Criteria for Abnormal Behavior\n- **Statistical Infrequency**: Behavior that is rare or unusual\n- **Deviation from Social Norms**: Violates cultural expectations\n- **Personal Distress**: Causes significant emotional distress\n- **Impaired Functioning**: Interferes with daily activities\n\n### Cultural Considerations\n- Behavior must be evaluated within cultural context\n- What's normal varies across cultures\n- Culture-bound syndromes exist in specific cultures\n\n## Classification System: DSM-5\n\n### Purpose and Structure\n- **Diagnostic and Statistical Manual of Mental Disorders**\n- Provides standardized criteria for diagnosis\n- Multiaxial system for comprehensive assessment\n- Regular updates based on research\n\n## Major Disorder Categories\n\n### Anxiety Disorders\n- **Generalized Anxiety Disorder**: Excessive worry about multiple areas\n- **Panic Disorder**: Recurrent panic attacks\n- **Phobias**: Irrational fears of specific objects or situations\n- **Social Anxiety Disorder**: Fear of social situations\n\n### Mood Disorders\n- **Major Depressive Disorder**: Persistent sadness and loss of interest\n- **Bipolar Disorder**: Alternating episodes of mania and depression\n- **Dysthymia**: Chronic, mild depression\n\n### Schizophrenia Spectrum\n- **Positive Symptoms**: Hallucinations, delusions, disorganized thinking\n- **Negative Symptoms**: Flat affect, social withdrawal, reduced motivation\n- **Cognitive Symptoms**: Attention and memory problems\n\n### Personality Disorders\n- **Cluster A**: Odd or eccentric (paranoid, schizoid, schizotypal)\n- **Cluster B**: Dramatic or erratic (antisocial, borderline, histrionic, narcissistic)\n- **Cluster C**: Anxious or fearful (avoidant, dependent, obsessive-compulsive)\n\n### Trauma and Stressor-Related Disorders\n- **PTSD**: Post-traumatic stress disorder\n- **Acute Stress Disorder**: Short-term trauma response\n- **Adjustment Disorders**: Difficulty adapting to stressful events\n\n## Causes of Psychological Disorders\n\n### Biological Factors\n- **Genetics**: Inherited vulnerability to mental illness\n- **Neurotransmitters**: Chemical imbalances in the brain\n- **Brain Structure**: Abnormalities in brain anatomy\n- **Medical Conditions**: Physical illnesses affecting mental health\n\n### Psychological Factors\n- **Learning**: Conditioned responses and maladaptive behaviors\n- **Cognitive**: Distorted thinking patterns and beliefs\n- **Personality**: Individual differences in coping and resilience\n- **Trauma**: Adverse childhood experiences and life events\n\n### Social Factors\n- **Family**: Dysfunctional family dynamics and relationships\n- **Socioeconomic Status**: Poverty and social disadvantage\n- **Cultural**: Cultural attitudes toward mental illness\n- **Social Support**: Availability of supportive relationships\n\n## Treatment Approaches\n\n### Biological Treatments\n- **Medications**: Antidepressants, antipsychotics, anxiolytics\n- **ECT**: Electroconvulsive therapy for severe depression\n- **Neurostimulation**: TMS, DBS for treatment-resistant conditions\n\n### Psychological Treatments\n- **Cognitive-Behavioral Therapy**: Changing thoughts and behaviors\n- **Psychodynamic Therapy**: Exploring unconscious conflicts\n- **Humanistic Therapy**: Client-centered and experiential approaches\n\n### Prevention and Stigma\n- **Primary Prevention**: Preventing disorders before they occur\n- **Secondary Prevention**: Early identification and intervention\n- **Tertiary Prevention**: Preventing relapse and disability\n- **Reducing Stigma**: Education and awareness campaigns\n\nUnderstanding psychological disorders is crucial for promoting mental health and reducing suffering.`
        }
      ],
      quiz: {
        id: "abnormal-psychology-quiz",
        title: "Abnormal Psychology Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which criterion for abnormality focuses on whether behavior interferes with daily life?",
            options: ["Statistical infrequency", "Personal distress", "Impaired functioning", "Social norms"],
            correctAnswer: 2,
            explanation: "Impaired functioning refers to behavior that significantly interferes with a person's ability to carry out daily activities and responsibilities."
          }
        ]
      }
    },

    {
      id: "developmental-psychology",
      title: "Developmental Psychology",
      description: "Human development across the lifespan from infancy to old age",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "developmental-theories",
          title: "Theories of Human Development",
          description: "Major theories explaining how people change across the lifespan",
          type: "video",
          duration: 35
        },
        {
          id: "lifespan-development",
          title: "Development Across the Lifespan",
          description: "Physical, cognitive, and social development from birth to death",
          type: "text",
          duration: 50,
          textContent: `# Development Across the Lifespan\n\n## Major Developmental Theories\n\n### Piaget's Cognitive Development\n- **Sensorimotor Stage** (0-2 years): Learning through senses and motor actions\n- **Preoperational Stage** (2-7 years): Symbolic thinking, egocentrism\n- **Concrete Operational** (7-11 years): Logical thinking about concrete objects\n- **Formal Operational** (11+ years): Abstract and hypothetical thinking\n\n### Erikson's Psychosocial Development\n- **Eight Stages**: Each with a developmental crisis to resolve\n- **Trust vs. Mistrust** (infancy): Basic trust in caregivers\n- **Autonomy vs. Shame** (toddlerhood): Independence and self-control\n- **Identity vs. Role Confusion** (adolescence): Personal identity formation\n- **Generativity vs. Stagnation** (middle adulthood): Contributing to society\n\n### Vygotsky's Sociocultural Theory\n- **Zone of Proximal Development**: Gap between current and potential ability\n- **Scaffolding**: Adult support that gradually decreases\n- **Cultural Tools**: Language and symbols for cognitive development\n\n## Physical Development\n\n### Prenatal Development\n- **Germinal Period** (0-2 weeks): Cell division and implantation\n- **Embryonic Period** (2-8 weeks): Organ formation\n- **Fetal Period** (8 weeks-birth): Growth and maturation\n- **Teratogens**: Environmental factors that can harm development\n\n### Childhood Physical Development\n- **Gross Motor Skills**: Large muscle movements (walking, running)\n- **Fine Motor Skills**: Small muscle control (writing, drawing)\n- **Brain Development**: Rapid growth and neural pruning\n\n### Adolescent Physical Development\n- **Puberty**: Hormonal changes and sexual maturation\n- **Growth Spurt**: Rapid increase in height and weight\n- **Brain Development**: Prefrontal cortex maturation continues\n\n### Adult Physical Development\n- **Early Adulthood**: Peak physical functioning\n- **Middle Age**: Gradual decline, menopause, presbyopia\n- **Late Adulthood**: Continued decline, increased health issues\n\n## Cognitive Development\n\n### Language Development\n- **Babbling** (6 months): Repetitive consonant-vowel combinations\n- **First Words** (12 months): Single word utterances\n- **Two-Word Stage** (18-24 months): Telegraphic speech\n- **Grammar Explosion** (2-5 years): Rapid rule learning\n\n### Memory Development\n- **Infantile Amnesia**: Limited memory for early experiences\n- **Working Memory**: Gradual improvement throughout childhood\n- **Strategies**: Development of rehearsal and organization\n\n### Theory of Mind\n- **Understanding Others' Mental States**: Around age 4\n- **False Belief Tasks**: Understanding that others can be wrong\n- **Social Implications**: Empathy and perspective-taking\n\n## Social and Emotional Development\n\n### Attachment Theory (Bowlby)\n- **Secure Attachment**: Confident exploration, comfort with caregiver\n- **Insecure-Avoidant**: Independence, little distress at separation\n- **Insecure-Ambivalent**: Clingy behavior, difficult to comfort\n- **Disorganized**: Inconsistent, confused attachment behaviors\n\n### Identity Development\n- **Adolescent Identity Crisis**: Exploration of roles and values\n- **Identity Achievement**: Commitment after exploration\n- **Moratorium**: Active exploration without commitment\n- **Foreclosure**: Commitment without exploration\n- **Diffusion**: Neither exploration nor commitment\n\n### Moral Development (Kohlberg)\n- **Preconventional**: Punishment avoidance and self-interest\n- **Conventional**: Social approval and law-and-order\n- **Postconventional**: Universal principles and justice\n\n## Aging and Later Life\n\n### Cognitive Changes\n- **Crystallized Intelligence**: Accumulated knowledge and skills (stable)\n- **Fluid Intelligence**: Processing speed and working memory (declines)\n- **Wisdom**: Integration of knowledge, experience, and judgment\n\n### Social Changes\n- **Retirement**: Role transitions and lifestyle changes\n- **Grandparenthood**: New family roles and relationships\n- **Social Networks**: Changes in friendship and social support\n\n### Death and Dying\n- **Kübler-Ross Stages**: Denial, anger, bargaining, depression, acceptance\n- **End-of-Life Issues**: Advanced directives, hospice care\n- **Bereavement**: Grief process and coping with loss\n\nDevelopmental psychology shows that growth and change occur throughout life, with both continuity and change characterizing human development.`
        }
      ],
      quiz: {
        id: "developmental-psychology-quiz",
        title: "Developmental Psychology Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Piaget, during which stage do children develop the ability to think abstractly?",
            options: ["Sensorimotor", "Preoperational", "Concrete operational", "Formal operational"],
            correctAnswer: 3,
            explanation: "The formal operational stage (beginning around age 11) is when children develop the ability to think abstractly and use hypothetical reasoning."
          }
        ]
      }
    },

    {
      id: "social-psychology-basics",
      title: "Social Psychology Fundamentals",
      description: "How social context influences thoughts, feelings, and behavior",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "social-cognition",
          title: "Social Cognition and Attribution",
          description: "How we think about ourselves and others in social situations",
          type: "video",
          duration: 30
        },
        {
          id: "attitudes-and-persuasion",
          title: "Attitudes and Persuasion",
          description: "Formation and change of attitudes and beliefs",
          type: "text",
          duration: 45,
          textContent: `# Attitudes and Persuasion\n\n## Understanding Attitudes\n\n### Components of Attitudes\n- **Cognitive**: Beliefs and thoughts about the attitude object\n- **Affective**: Emotional reactions and feelings\n- **Behavioral**: Actions and behavioral intentions\n\n### Attitude Formation\n- **Direct Experience**: Personal interaction with attitude object\n- **Social Learning**: Observing others and modeling\n- **Classical Conditioning**: Association with positive or negative stimuli\n- **Cognitive Evaluation**: Rational analysis of information\n\n## Attribution Theory\n\n### Internal vs. External Attributions\n- **Internal (Dispositional)**: Behavior due to personal characteristics\n- **External (Situational)**: Behavior due to environmental factors\n\n### Attribution Biases\n- **Fundamental Attribution Error**: Overestimate internal causes for others\n- **Actor-Observer Bias**: Different attributions for self vs. others\n- **Self-Serving Bias**: Internal attributions for success, external for failure\n\n## Persuasion and Attitude Change\n\n### Elaboration Likelihood Model\n- **Central Route**: Careful evaluation of arguments and evidence\n- **Peripheral Route**: Focus on superficial cues (source credibility, attractiveness)\n- **Factors Determining Route**: Motivation and ability to process\n\n### Persuasion Factors\n- **Source**: Credibility, attractiveness, similarity to audience\n- **Message**: Strength of arguments, emotional appeals, fear appeals\n- **Audience**: Intelligence, self-esteem, prior attitudes\n- **Context**: Distraction, mood, social setting\n\n### Resistance to Persuasion\n- **Inoculation**: Exposure to weak counterarguments builds resistance\n- **Reactance**: Opposition when freedom is threatened\n- **Selective Exposure**: Seeking confirming information\n- **Cognitive Dissonance**: Discomfort from inconsistent beliefs\n\n## Cognitive Dissonance Theory\n\n### Basic Principles\n- Discomfort from holding contradictory beliefs or attitudes\n- Motivation to reduce dissonance through attitude change\n- Greater dissonance when behavior conflicts with important attitudes\n\n### Applications\n- **Insufficient Justification**: Small rewards lead to more attitude change\n- **Post-Decision Dissonance**: Increasing attractiveness of chosen option\n- **Effort Justification**: Valuing difficult-to-obtain goals more highly\n\n## Social Influence\n\n### Conformity\n- **Asch Line Studies**: Pressure to conform to group judgment\n- **Factors**: Group size, unanimity, public vs. private responses\n- **Cultural Differences**: Collectivist vs. individualist cultures\n\n### Compliance\n- **Foot-in-the-Door**: Small request followed by larger request\n- **Door-in-the-Face**: Large request followed by smaller request\n- **Low-Ball**: Agreement followed by increased costs\n\n### Obedience\n- **Milgram Studies**: Obedience to authority figures\n- **Factors**: Proximity to authority, legitimacy of authority, gradual escalation\n- **Ethical Issues**: Participant welfare and informed consent\n\n## Group Behavior\n\n### Group Decision Making\n- **Group Polarization**: Groups make more extreme decisions\n- **Groupthink**: Desire for harmony leads to poor decisions\n- **Social Loafing**: Reduced effort in group settings\n\n### Intergroup Relations\n- **In-group Favoritism**: Preference for own group members\n- **Out-group Homogeneity**: Viewing out-group members as similar\n- **Realistic Conflict Theory**: Competition creates intergroup hostility\n- **Contact Hypothesis**: Intergroup contact can reduce prejudice\n\nSocial psychology reveals how profoundly social situations influence our thoughts, feelings, and behaviors, often in ways we don't realize.`
        }
      ],
      quiz: {
        id: "social-psychology-basics-quiz",
        title: "Social Psychology Fundamentals Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "The fundamental attribution error refers to the tendency to:",
            options: [
              "Overestimate situational factors when explaining others' behavior",
              "Overestimate dispositional factors when explaining others' behavior",
              "Underestimate our own role in situations",
              "Make accurate attributions about behavior"
            ],
            correctAnswer: 1,
            explanation: "The fundamental attribution error is the tendency to overestimate dispositional (internal) factors and underestimate situational (external) factors when explaining others' behavior."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "psychology-research-proposal",
      title: "Psychology Research Proposal",
      description: "Design and write a research proposal for a psychological study",
      type: "project",
      dueWeeks: 10,
      maxScore: 100,
      instructions: "Develop a complete research proposal including literature review, methodology, ethical considerations, and expected outcomes for a study in an area of psychology that interests you.",
      submissionFormat: "pdf",
      resources: [
        "APA style guidelines",
        "Psychology research databases",
        "Ethics guidelines",
        "Statistical analysis resources"
      ]
    }
  ],

  finalExam: {
    id: "psychology-final-exam",
    title: "Introduction to Psychology Final Examination",
    description: "Comprehensive examination covering all major areas of psychology including research methods, biological bases, cognition, and applications",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which research method would be most appropriate for studying the long-term effects of childhood trauma on adult mental health?",
        options: [
          "Laboratory experiment",
          "Cross-sectional survey",
          "Longitudinal study",
          "Case study"
        ],
        correctAnswer: 2,
        explanation: "A longitudinal study follows the same participants over time, making it ideal for studying long-term effects and developmental changes."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "The neurotransmitter most associated with depression is:",
        options: [
          "Dopamine",
          "Acetylcholine",
          "Serotonin",
          "GABA"
        ],
        correctAnswer: 2,
        explanation: "Serotonin deficiency is strongly linked to depression, and many antidepressant medications work by increasing serotonin availability in the brain."
      }
    ]
  },

  quizzes: []
};